import React, { useState, useEffect, useMemo } from 'react';

import { http } from '../../lib/http';
import { Search, RefreshCw, Calendar, Filter, ArrowUpDown, AlertTriangle, Clock } from 'lucide-react';

const RenewalsTab = ({ setMessage, setError }) => {

    const [renewals, setRenewals] = useState([]);
    const [loading, setLoading] = useState(false);
    const [counts, setCounts] = useState({ due_today: 0, overdue: 0, upcoming: 0 });
    const [activeView, setActiveView] = useState('due_today'); // due_today, upcoming, overdue, specific_date
    const [specificDate, setSpecificDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [processing, setProcessing] = useState({});

    // Enhanced filtering state
    const [searchTerm, setSearchTerm] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
    const [sortBy, setSortBy] = useState('renewal_date');
    const [sortOrder, setSortOrder] = useState('asc');
    const [upcomingDays, setUpcomingDays] = useState(7);

    const loadRenewals = async () => {
        setLoading(true);
        try {
            let url = `/admin/renewals?view=${activeView}`;
            if (activeView === 'specific_date') {
                url += `&date=${specificDate}`;
            }
            if (activeView === 'upcoming') {
                url += `&days=${upcomingDays}`;
            }
            const response = await http.get(url);
            setRenewals(response.data.data?.renewals || response.data.renewals || []);
        } catch (error) {
            console.error('Failed to load renewals:', error);
            setError(error.response?.data?.detail || 'Failed to load renewals');
        } finally {
            setLoading(false);
        }
    };

    const loadCounts = async () => {
        try {
            const res = await http.get('/admin/renewals/counts');
            setCounts(res.data.data || res.data);
        } catch (error) {
            console.error('Failed to load renewal counts:', error);
        }
    };

    // Client-side filtering and sorting
    const filteredAndSortedRenewals = useMemo(() => {
        let filtered = [...renewals];

        // Search filter
        if (searchTerm.trim()) {
            const search = searchTerm.toLowerCase();
            filtered = filtered.filter(r =>
                r.first_name?.toLowerCase().includes(search) ||
                r.last_name?.toLowerCase().includes(search) ||
                r.email?.toLowerCase().includes(search) ||
                `${r.first_name} ${r.last_name}`.toLowerCase().includes(search)
            );
        }

        // Payment status filter
        if (paymentStatusFilter !== 'all') {
            filtered = filtered.filter(r => r.payment_status?.toLowerCase() === paymentStatusFilter.toLowerCase());
        }

        // Sorting
        filtered.sort((a, b) => {
            let comparison = 0;

            switch (sortBy) {
                case 'renewal_date':
                    comparison = new Date(a.plan_renewal_date || 0) - new Date(b.plan_renewal_date || 0);
                    break;
                case 'name':
                    comparison = `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`);
                    break;
                case 'amount':
                    comparison = (parseFloat(a.total_amount) || 0) - (parseFloat(b.total_amount) || 0);
                    break;
                default:
                    comparison = 0;
            }

            return sortOrder === 'asc' ? comparison : -comparison;
        });

        return filtered;
    }, [renewals, searchTerm, paymentStatusFilter, sortBy, sortOrder]);

    useEffect(() => {
        loadRenewals();
        loadCounts(); // Load counts when tab opens
    }, [activeView, specificDate, upcomingDays]);

    const formatCurrency = (amount) => {
        const num = parseFloat(amount) || 0;
        return `$${num.toFixed(2)}`;
    };

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="text-lg font-medium">Loading renewals...</div>
            </div>
        );
    }

    // Helper to check if a date is today
    const isToday = (dateStr) => {
        if (!dateStr) return false;
        const today = new Date().toISOString().split('T')[0];
        return dateStr === today;
    };

    // Helper to check if date is overdue
    const isOverdue = (dateStr) => {
        if (!dateStr) return false;
        const today = new Date().toISOString().split('T')[0];
        return dateStr < today;
    };

    // Toggle sort order
    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    return (
        <div className="space-y-6">
            {/* Summary Cards - Prominent Display */}
            <div className="grid grid-cols-3 gap-4">
                <div
                    onClick={() => setActiveView('due_today')}
                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${activeView === 'due_today'
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-200 bg-white hover:border-red-300'
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <AlertTriangle className={`h-5 w-5 ${counts.due_today > 0 ? 'text-red-500' : 'text-gray-400'}`} />
                        <span className="text-sm font-medium text-gray-600">Due Today</span>
                    </div>
                    <p className={`text-3xl font-bold mt-2 ${counts.due_today > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                        {counts.due_today}
                    </p>
                    {counts.due_today > 0 && (
                        <p className="text-xs text-red-500 mt-1">⚠️ Requires immediate attention!</p>
                    )}
                </div>

                <div
                    onClick={() => setActiveView('overdue')}
                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${activeView === 'overdue'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 bg-white hover:border-orange-300'
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <Clock className={`h-5 w-5 ${counts.overdue > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
                        <span className="text-sm font-medium text-gray-600">Overdue</span>
                    </div>
                    <p className={`text-3xl font-bold mt-2 ${counts.overdue > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                        {counts.overdue}
                    </p>
                </div>

                <div
                    onClick={() => setActiveView('upcoming')}
                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${activeView === 'upcoming'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-blue-300'
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-blue-500" />
                        <span className="text-sm font-medium text-gray-600">Upcoming (7 days)</span>
                    </div>
                    <p className="text-3xl font-bold mt-2 text-gray-900">{counts.upcoming}</p>
                </div>

            </div>

            {/* Header with Refresh */}
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold text-white">
                        {activeView === 'due_today' && '🔴 Renewals Due Today'}
                        {activeView === 'overdue' && '🟠 Overdue Renewals'}
                        {activeView === 'upcoming' && '🔵 Upcoming Renewals'}
                        {activeView === 'specific_date' && '📅 Renewals for Specific Date'}
                        {' '}({filteredAndSortedRenewals.length})
                    </h3>
                    <p className="text-gray-400 text-sm">
                        {activeView === 'due_today' && 'Plans expiring today - send reminders immediately'}
                        {activeView === 'overdue' && 'Plans past their renewal date'}
                        {activeView === 'upcoming' && `Plans expiring in the next ${upcomingDays} days`}
                        {activeView === 'specific_date' && `Plans expiring on ${specificDate}`}
                    </p>
                </div>
                <button
                    onClick={() => { loadRenewals(); loadCounts(); }}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2"
                >
                    <RefreshCw size={16} />
                    Refresh
                </button>
            </div>

            {/* View Filter Tabs */}
            <div className="flex flex-wrap gap-2 border-b border-gray-700 pb-2 items-end">
                <button
                    onClick={() => setActiveView('due_today')}
                    className={`px-4 py-2 font-medium transition-colors rounded-t-lg ${activeView === 'due_today'
                        ? 'bg-red-500 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }`}
                >
                    Due Today {counts.due_today > 0 && <span className="ml-1 bg-white text-red-500 text-xs font-bold px-2 py-0.5 rounded-full">{counts.due_today}</span>}
                </button>
                <button
                    onClick={() => setActiveView('overdue')}
                    className={`px-4 py-2 font-medium transition-colors rounded-t-lg ${activeView === 'overdue'
                        ? 'bg-orange-500 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }`}
                >
                    Overdue {counts.overdue > 0 && <span className="ml-1 bg-white text-orange-500 text-xs font-bold px-2 py-0.5 rounded-full">{counts.overdue}</span>}
                </button>
                <button
                    onClick={() => setActiveView('upcoming')}
                    className={`px-4 py-2 font-medium transition-colors rounded-t-lg ${activeView === 'upcoming'
                        ? 'bg-blue-500 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }`}
                >
                    Upcoming
                </button>
                <button
                    onClick={() => setActiveView('specific_date')}
                    className={`px-4 py-2 font-medium transition-colors rounded-t-lg ${activeView === 'specific_date'
                        ? 'bg-purple-500 text-white'
                        : 'text-gray-400 hover:text-white hover:bg-gray-700'
                        }`}
                >
                    Specific Date
                </button>
                {/* Date picker for specific date */}
                {activeView === 'specific_date' && (
                    <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium text-white">Date:</label>
                        <input
                            type="date"
                            value={specificDate}
                            onChange={(e) => setSpecificDate(e.target.value)}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white"
                        />
                    </div>
                )}

                {/* Days selector for upcoming */}
                {activeView === 'upcoming' && (
                    <div className="flex items-center gap-2 ml-4">
                        <label className="text-sm font-medium text-white">Next:</label>
                        <select
                            value={upcomingDays}
                            onChange={(e) => setUpcomingDays(parseInt(e.target.value))}
                            className="border border-gray-300 rounded-md px-3 py-1 text-sm bg-white"
                        >
                            <option value={3}>3 days</option>
                            <option value={7}>7 days</option>
                            <option value={14}>14 days</option>
                            <option value={30}>30 days</option>
                        </select>
                    </div>
                )}
            </div>

            {/* Filters Bar */}
            <div className="bg-gray-700 p-4 rounded-lg flex flex-wrap gap-4 items-center">
                {/* Search */}
                <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                            type="text"
                            placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        />
                    </div>
                </div>

                {/* Payment Status Filter */}
                <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-400" />
                    <select
                        value={paymentStatusFilter}
                        onChange={(e) => setPaymentStatusFilter(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                    >
                        <option value="all">All Payment Status</option>
                        <option value="Paid">Paid</option>
                        <option value="Pending">Pending</option>
                        <option value="Partial">Partial</option>
                    </select>
                </div>

                {/* Sort Options */}
                <div className="flex items-center gap-2">
                    <ArrowUpDown className="h-4 w-4 text-gray-400" />
                    <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                    >
                        <option value="renewal_date">Sort by Renewal Date</option>
                        <option value="name">Sort by Name</option>
                        <option value="amount">Sort by Amount</option>
                    </select>
                    <button
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        className="px-2 py-2 bg-white border border-gray-300 rounded-md text-sm hover:bg-gray-100"
                        title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                    >
                        {sortOrder === 'asc' ? '↑' : '↓'}
                    </button>
                </div>

                {/* Clear Filters */}
                {(searchTerm || paymentStatusFilter !== 'all') && (
                    <button
                        onClick={() => {
                            setSearchTerm('');
                            setPaymentStatusFilter('all');
                        }}
                        className="text-red-400 hover:text-red-300 text-sm underline"
                    >
                        Clear Filters
                    </button>
                )}
            </div>

            {/* Results Info */}
            <div className="text-sm text-gray-400">
                Showing {filteredAndSortedRenewals.length} of {renewals.length} renewals
                {searchTerm && ` matching "${searchTerm}"`}
                {paymentStatusFilter !== 'all' && ` with ${paymentStatusFilter} status`}
            </div>

            {filteredAndSortedRenewals.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <h4 className="text-lg font-medium text-gray-900 mb-2">No renewals found</h4>
                    <p className="text-gray-700">
                        {searchTerm || paymentStatusFilter !== 'all'
                            ? 'Try adjusting your filters'
                            : `No customers in the ${activeView.replace('_', ' ')} view`
                        }
                    </p>
                </div>
            ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {filteredAndSortedRenewals.map((renewal) => (
                        <div
                            key={renewal.customer_id}
                            className={`bg-white rounded-lg border-2 p-4 shadow-sm transition-all ${isToday(renewal.plan_renewal_date)
                                ? 'border-red-500 ring-2 ring-red-200'
                                : isOverdue(renewal.plan_renewal_date)
                                    ? 'border-orange-400'
                                    : 'border-gray-200'
                                }`}
                        >
                            {/* Urgent Badge */}
                            {isToday(renewal.plan_renewal_date) && (
                                <div className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 inline-block">
                                    🔴 EXPIRES TODAY
                                </div>
                            )}
                            {isOverdue(renewal.plan_renewal_date) && !isToday(renewal.plan_renewal_date) && (
                                <div className="bg-orange-500 text-white text-xs font-bold px-2 py-1 rounded mb-2 inline-block">
                                    ⚠️ OVERDUE
                                </div>
                            )}

                            {/* Customer Info */}
                            <div className="mb-4">
                                <h4 className="font-semibold text-gray-900">{renewal.first_name} {renewal.last_name}</h4>
                                <p className="text-sm text-gray-600">{renewal.email}</p>
                                <div className="mt-2 space-y-1 text-sm">
                                    <div><span className="text-gray-500">Plan Key:</span> <span className="text-gray-900">{renewal.plan_key}</span></div>
                                    <div><span className="text-gray-500">Plan Term:</span> <span className="text-gray-900">{renewal.plan_term}</span></div>
                                    <div>
                                        <span className="text-gray-500">Renewal Date:</span>{' '}
                                        <span className={`font-medium ${isToday(renewal.plan_renewal_date)
                                            ? 'text-red-600'
                                            : isOverdue(renewal.plan_renewal_date)
                                                ? 'text-orange-600'
                                                : 'text-gray-900'
                                            }`}>
                                            {renewal.plan_renewal_date}
                                        </span>
                                    </div>

                                    <div><span className="text-gray-900">Payment Status:</span>
                                        <span className={`ml-1 px-2 py-0.5 rounded text-xs font-medium uppercase ${renewal.payment_status?.toLowerCase() === 'paid' ? 'bg-green-500 text-white' :
                                            renewal.payment_status?.toLowerCase() === 'pending' ? 'bg-orange-500 text-white' :
                                                'bg-red-500 text-white'
                                            }`}>
                                            {renewal.payment_status}
                                        </span>
                                    </div>
                                    <div><span className="text-gray-900">Balance Due:</span> {formatCurrency(renewal.balance_due)}</div>
                                    {renewal.add_ons && <div><span className="text-gray-900">Add-ons:</span> {renewal.add_ons}</div>}
                                    <div><span className="text-gray-900">Total Amount:</span> <span className="font-medium">{formatCurrency(renewal.total_amount)}</span></div>
                                </div>
                            </div>

                            {renewal.paused && (
                                <div className="bg-red-100 border-2 border-red-500 rounded p-2 text-xs text-red-700 font-medium">
                                    ⚠️ Plan Paused - Resume before renewal
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}


        </div>
    );
};

export default RenewalsTab;
